import { useSystemInfo, useSystemViewData, useSystemHeadlines } from "../providers/helpers/useSystem"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import InstallationMonitoringView from "@hydrologiq/shared/installation/InstallationMonitoringView"
import combinedGraphs from "@hydrologiq/shared/asset/monitoring/graphs/Combined.json"
import combinedFigures from "../components/monitoring/FiguresDefault.json"
import { AssetConfig } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import { Graph, Figure } from "@hydrologiq/schemas/1.0.0/installationPage"
import { Skeleton, Box, Text, VStack } from "@chakra-ui/react"
import { useMemo } from "react"
import TimescaleMonitoringView from "../components/monitoring/TimescaleMonitoringView"
import SystemHeadlineFigures from "./SystemHeadlineFigures"

import { isHydrogenSystem, isHydrogenFigure, isHydrogenGraph } from "../utils/hydrogenSystem"
export { isHydrogenSystem, isHydrogenFigure, isHydrogenGraph } from "../utils/hydrogenSystem"

const SystemMonitoring = () => {
  const info = useSystemInfo()
  const systemViewData = useSystemViewData()
  const headlines = useSystemHeadlines()

  let route = useRoute()
  route = route.endsWith("/") ? route : `${route}/`

  const isHydrogen = useMemo(() => {
    return isHydrogenSystem(systemViewData, info)
  }, [systemViewData, info])

  const figures = useMemo(() => {
    const rawFigures =
      info?.figures && info.figures.length > 0
        ? (info.figures as Figure[])
        : (combinedFigures as Figure[])

    if (isHydrogen) {
      return rawFigures
    }
    return rawFigures.filter((f) => !isHydrogenFigure(f))
  }, [info?.figures, isHydrogen])

  const fallbackGraphs = useMemo(() => {
    const rawGraphs =
      info?.graphs && info.graphs.length > 0
        ? (info.graphs as Graph[])
        : (combinedGraphs as Graph[])

    if (isHydrogen) {
      return rawGraphs
    }
    return rawGraphs.filter((g) => !isHydrogenGraph(g))
  }, [info?.graphs, isHydrogen])

  const timescaleFields = useMemo(() => {
    return systemViewData?.system?.timescaleFields ?? []
  }, [systemViewData?.system?.timescaleFields])

  const availableAssetClasses = useMemo(() => {
    if (!systemViewData) return undefined

    const classes = new Set<string>()

    // A hybrid's Modules (its battery, fuel tank, genset) have no node of their
    // own, so their classes come from the parent node's child assets.
    const addNodeClasses = (node: { asset?: unknown; childAssets?: Array<{ assetClass: string | null }> }) => {
      const assetClass = (node.asset as any)?.class
      if (assetClass) classes.add(assetClass)
      for (const child of node.childAssets ?? []) {
        if (child.assetClass) classes.add(child.assetClass)
      }
    }

    // Collect from installation nodes
    for (const group of systemViewData.installations ?? []) {
      for (const node of group.nodes ?? []) addNodeClasses(node)
    }

    // Collect from isolated nodes
    for (const node of systemViewData.isolatedNodes ?? []) addNodeClasses(node)

    return Array.from(classes)
  }, [systemViewData])

  // Guard: show skeleton until system data is available
  if (!systemViewData?.system) {
    return <Skeleton height="100%" width="100%" />
  }

  const source = systemViewData.system.source ?? "timescale"

  const [installation, installationInfo] =
    info?.inlineMonitoring || !info?.installations || Object.keys(info?.installations || {}).length === 0
      ? [undefined, undefined]
      : Object.entries(info?.installations)[0]

  // Define asset configurations for the system
  const assetEntries = Object.entries(info?.assets || {})
  const assetConfigs: AssetConfig[] = [
    ...assetEntries
      .filter(([_assetId, asset]) => {
        // Exclude fuel tanks - they should use category-based colors (green spectrum)
        // Fuel tanks are identified by "Tank" in their name
        const isFuelTank = asset.name?.toLowerCase().includes('tank')
        return !isFuelTank
      })
      .map(([_assetId, asset]) => ({
        assetId: _assetId,
        label: asset.name,
        strokeWidth: 2,
        dot: false,
        connectNulls: true,
        group: asset.location?.name || undefined,
      })),
  ]

  // If source is 'timescale' (default), use the TimescaleDB monitoring view.
  // Otherwise fall back to the Influx view.
  if (source === "timescale" && systemViewData?.system?.id) {
    return (
      <TimescaleMonitoringView
        systemId={systemViewData.system.id}
        timescaleFields={timescaleFields}
        graphs={info?.graphs as Graph[] | undefined}
        assetConfigs={assetConfigs}
        installationStartDate={installationInfo?.startDate}
        installationEndDate={installationInfo?.endDate}
        installationId={installation}
        figures={figures}
        availableAssetClasses={availableAssetClasses}
        headlineSlot={
          <SystemHeadlineFigures
            headlines={headlines}
            loading={false}
            figureGridMb={0}
            figures={figures}
          />
        }
      />
    )
  }

  // source == 'influx': use the existing Influx-backed monitoring view.
  // If there is no installation available (e.g. after a data connection was
  // removed) but we have timescale field data, fall through to the timescale
  // view so historical monitoring data remains visible.
  if (installation && installationInfo) {
    return (
      <InstallationMonitoringView
        installation={installation}
        installationStartDate={installationInfo.startDate}
        installationEndDate={installationInfo.endDate}
        graphs={fallbackGraphs}
        figures={figures}
        assetConfigs={assetConfigs}
        useNewHeadlines={true}
      />
    )
  }

  // No installation for influx view — fall back to timescale if data exists
  if (timescaleFields.length > 0 && systemViewData?.system?.id) {
    return (
      <TimescaleMonitoringView
        systemId={systemViewData.system.id}
        timescaleFields={timescaleFields}
        graphs={info?.graphs as Graph[] | undefined}
        assetConfigs={assetConfigs}
        figures={figures}
        availableAssetClasses={availableAssetClasses}
        headlineSlot={
          <SystemHeadlineFigures
            headlines={headlines}
            loading={false}
            figureGridMb={0}
            figures={figures}
          />
        }
      />
    )
  }

  return (
    <Box p={8} textAlign="center">
      <VStack gap={3}>
        <Text fontSize="lg" fontWeight="medium" color="fg.muted">
          No monitoring data available
        </Text>
        <Text fontSize="sm" color="fg.subtle">
          Activate a data connection to begin receiving monitoring data. If you have recently connected a data source, data should be available within 10 minutes.
        </Text>
      </VStack>
    </Box>
  )
}

export default SystemMonitoring
